/**
 * Catalog categories admin list (ADMIN-PR2-a) — GET /v1/categories + create.
 * Category writes are super_admin (server-enforced). Rows link to detail/edit.
 *
 * The list is grouped by domain family (`category_group`, server-owned). Each family
 * is a collapsible accordion section with a catalog count; open/closed is remembered
 * per family in localStorage. A search box flattens the groups while typing.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { createCategory, listCategories, type CategoryOut } from "../auth/mastersApi";
import { GROUP_ORDER, groupCategories, OTHER_GROUP } from "../lib/catalogGroups";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";
import { JsonField, parseJsonObject } from "../components/JsonField";

const OPEN_STATE_KEY = "oxbo-admin:catalog-groups-open";

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function loadOpenState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(OPEN_STATE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function matches(c: CategoryOut, q: string): boolean {
  const needle = q.toLowerCase();
  return (
    c.category_key.toLowerCase().includes(needle) ||
    c.display_name.toLowerCase().includes(needle) ||
    (c.category_group ?? "").toLowerCase().includes(needle)
  );
}

export function CategoriesPage(): ReactNode {
  const { data, loading, error, reload } = useAsync<CategoryOut[]>(() => listCategories(), []);
  const [showForm, setShowForm] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [openState, setOpenState] = useState<Record<string, boolean>>(loadOpenState);

  // A group is open unless it has been explicitly collapsed (default-open for discovery).
  const isOpen = (group: string) => openState[group] !== false;
  const toggleGroup = (group: string) =>
    setOpenState((prev) => {
      const next = { ...prev, [group]: prev[group] === false };
      try {
        localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota/availability errors — open-state is a nicety */
      }
      return next;
    });

  const q = query.trim();
  const groups = useMemo(() => groupCategories(data ?? []), [data]);
  const filtered = useMemo(
    () => (q ? (data ?? []).filter((c) => matches(c, q)) : []),
    [data, q],
  );

  return (
    <div>
      <PageHeader title="Catalog categories">
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
        >
          {showForm ? "Close" : "New category"}
        </button>
      </PageHeader>

      {showForm && (
        <CreateCategoryForm
          knownGroups={GROUP_ORDER}
          onCreated={() => {
            setShowForm(false);
            reload();
          }}
          onError={setMutationError}
        />
      )}

      {mutationError && (
        <div className="mb-3">
          <ErrorBanner message={mutationError} />
        </div>
      )}

      {!loading && !error && data && data.length > 0 && (
        <div className="mb-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search catalogs by name, key, or family…"
            aria-label="Search catalogs"
            className="w-full max-w-md rounded border border-slate-300 px-3 py-1.5 text-sm"
          />
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.length === 0 ? (
        <EmptyState message="No categories yet." />
      ) : q ? (
        filtered.length === 0 ? (
          <EmptyState message={`No catalogs match "${q}".`} />
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <CategoryTable rows={filtered} showGroup />
          </div>
        )
      ) : (
        <div className="space-y-3">
          {groups.map(({ group, categories }) => (
            <section key={group} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                aria-expanded={isOpen(group)}
                className="flex w-full items-center justify-between bg-slate-50 px-3 py-2 text-left hover:bg-slate-100"
              >
                <span className="flex items-center gap-2">
                  <span className={`text-slate-400 transition-transform ${isOpen(group) ? "rotate-90" : ""}`}>
                    ▸
                  </span>
                  <span className={`font-medium ${group === OTHER_GROUP ? "text-slate-500" : "text-slate-700"}`}>
                    {group}
                  </span>
                </span>
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {categories.length}
                </span>
              </button>
              {isOpen(group) && <CategoryTable rows={categories} />}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryTable({ rows, showGroup = false }: { rows: CategoryOut[]; showGroup?: boolean }): ReactNode {
  return (
    <table className="w-full text-left text-sm">
      <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
        <tr>
          <th className="px-3 py-2 font-medium">Key</th>
          <th className="px-3 py-2 font-medium">Display name</th>
          {showGroup && <th className="px-3 py-2 font-medium">Family</th>}
          <th className="px-3 py-2 font-medium">Override levels</th>
          <th className="px-3 py-2 font-medium">State</th>
          <th className="px-3 py-2 font-medium">Modified</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.category_key} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
            <td className="px-3 py-2 font-mono text-xs">
              <Link to={`/catalog/${encodeURIComponent(c.category_key)}`} className="text-oxbo hover:underline">
                {c.category_key}
              </Link>
            </td>
            <td className="px-3 py-2">{c.display_name}</td>
            {showGroup && <td className="px-3 py-2 text-slate-600">{c.category_group ?? OTHER_GROUP}</td>}
            <td className="px-3 py-2 text-slate-600">
              {c.override_levels.length ? c.override_levels.join(", ") : "—"}
            </td>
            <td className="px-3 py-2">
              <StatusBadge status={c.retired_at ? "retired" : "active"} />
            </td>
            <td className="px-3 py-2 text-slate-500">{fmtDate(c.modified_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CreateCategoryForm({
  knownGroups,
  onCreated,
  onError,
}: {
  knownGroups: readonly string[];
  onCreated: () => void;
  onError: (msg: string) => void;
}): ReactNode {
  const [categoryKey, setCategoryKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [categoryGroup, setCategoryGroup] = useState("");
  const [overrideLevels, setOverrideLevels] = useState("");
  const [schemaRaw, setSchemaRaw] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    onError("");
    const schema = parseJsonObject(schemaRaw);
    if (!schema.ok) {
      onError("property_schema is not valid JSON.");
      return;
    }
    setBusy(true);
    try {
      const group = categoryGroup.trim();
      await createCategory({
        category_key: categoryKey.trim(),
        display_name: displayName.trim(),
        ...(group ? { category_group: group } : {}),
        override_levels: overrideLevels
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        ...(schema.value ? { property_schema: schema.value } : {}),
      });
      onCreated();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-5 space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Category key</span>
          <input
            required
            value={categoryKey}
            onChange={(e) => setCategoryKey(e.target.value)}
            placeholder="mud_type"
            className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Display name</span>
          <input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Mud Type"
            className="w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Family (group, optional)</span>
          <input
            list="catalog-group-options"
            value={categoryGroup}
            onChange={(e) => setCategoryGroup(e.target.value)}
            placeholder="Rig"
            className="w-full rounded border border-slate-300 px-2 py-1.5"
          />
          <datalist id="catalog-group-options">
            {knownGroups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Override levels (comma-separated)</span>
          <input
            value={overrideLevels}
            onChange={(e) => setOverrideLevels(e.target.value)}
            placeholder="org, site_region"
            className="w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </label>
      </div>
      <JsonField
        label="property_schema (JSON, optional)"
        value={schemaRaw}
        onChange={setSchemaRaw}
        placeholder='{ "type": "object", "properties": { ... } }'
      />
      <button
        type="submit"
        disabled={busy}
        className="rounded bg-oxbo px-4 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark disabled:opacity-50"
      >
        {busy ? "Creating…" : "Create category"}
      </button>
    </form>
  );
}
